/**
 * TaskCompletionReasons - Cadastro de Motivos de Conclusão (Retenção / Jurídico)
 *
 * Lista, cria, edita e exclui os motivos usados ao concluir uma solicitação de
 * Retenção ou um processo Jurídico. Os motivos são escopados por módulo
 * (task_type) e por tenant; todo acesso ocorre via RPCs SECURITY DEFINER.
 *
 * @version 1.3.0
 * RELEASE: 2026-08-22 v1.3.0 - Campo "Resultado" exibido apenas no módulo
 *          Retenção; no Jurídico o motivo é salvo como 'neutral'
 * RELEASE: 2026-08-22 v1.2.0 - Switch "Retido" substituído pelo seletor de
 *          resultado (Retido / Cancelamento efetivado / Neutro)
 * RELEASE: 2026-08-22 v1.0.0 - Criação inicial
 */

import React, { useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, CheckCircle, ListChecks, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useTaskModule } from '@/hooks/useTaskModuleType';
import {
  useTaskCompletionReasons,
  type TaskCompletionReason,
  type TaskCompletionOutcome,
} from '@/hooks/useTaskCompletionReasons';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Resultados possíveis do motivo de conclusão.
 * - retained  : a retenção deu certo (entra no numerador da taxa)
 * - cancelled : cancelamento efetivado (entra apenas no denominador)
 * - neutral   : não conta para nenhum dos dois lados
 */
const OUTCOME_OPTIONS: Array<{ value: TaskCompletionOutcome; label: string; hint: string }> = [
  { value: 'retained', label: 'Retido', hint: 'Conta como cliente retido na taxa de retenção' },
  { value: 'cancelled', label: 'Cancelamento efetivado', hint: 'Conta como cancelamento na taxa de retenção' },
  { value: 'neutral', label: 'Neutro', hint: 'Fica fora do cálculo da taxa de retenção' },
];

/** Selo exibido na listagem conforme o resultado do motivo */
const OUTCOME_BADGE: Record<TaskCompletionOutcome, { label: string; className: string }> = {
  retained: { label: 'Retido', className: 'border-emerald-500/40 text-emerald-600' },
  cancelled: { label: 'Cancelamento efetivado', className: 'border-rose-500/40 text-rose-600' },
  neutral: { label: 'Neutro', className: 'border-muted-foreground/40 text-muted-foreground' },
};

/** Paleta sugerida para os motivos (tokens fixos de identificação visual) */
const COLOR_PRESETS = ['#64748b', '#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];


// ============================================================================
// KPI compacto (mesmo padrão das demais telas de cadastro de Tarefas)
// ============================================================================
const reasonVariantStyles = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
};

function MetricCard({
  title,
  value,
  icon: Icon,
  variant = 'primary',
}: {
  title: string;
  value: number;
  icon: React.ElementType;
  variant?: keyof typeof reasonVariantStyles;
}) {
  const styles = reasonVariantStyles[variant];
  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="truncate text-xs font-medium text-gray-900 dark:text-gray-100">{title}</p>
          </div>
          <p className="shrink-0 text-xl font-bold leading-tight text-gray-900 dark:text-gray-100">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TaskCompletionReasons() {
  const taskModule = useTaskModule();
  /** v1.3.0 — o campo "Resultado" alimenta a taxa de retenção, logo só vale em Retenção */
  const isRetention = taskModule.type === 'retention';
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  const { reasons, isLoading, upsertReason, isSaving, deleteReason } =
    useTaskCompletionReasons(taskModule.type);

  // ----------------------------------------------------------------- estado
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TaskCompletionReason | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formColor, setFormColor] = useState(COLOR_PRESETS[0]);
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [formIsActive, setFormIsActive] = useState(true);
  /** v1.2.0 — resultado do motivo (retido / cancelamento efetivado / neutro) */
  const [formOutcome, setFormOutcome] = useState<TaskCompletionOutcome>('cancelled');

  // ------------------------------------------------------------------- KPIs
  const kpis = useMemo(() => {
    const total = reasons.length;
    const active = reasons.filter((r) => r.is_active).length;
    const used = reasons.reduce((sum, r) => sum + Number(r.usage_count || 0), 0);
    return { total, active, used };
  }, [reasons]);

  // ---------------------------------------------------------------- handlers
  /** Abre o modal em modo criação */
  const handleCreate = () => {
    setEditing(null);
    setFormName('');
    setFormDescription('');
    setFormColor(COLOR_PRESETS[0]);
    setFormSortOrder(reasons.length);
    setFormIsActive(true);
    // v1.3.0 — Resultado só existe em Retenção; Jurídico é sempre neutro
    setFormOutcome(isRetention ? 'cancelled' : 'neutral');
    setDialogOpen(true);
  };

  /** Abre o modal em modo edição */
  const handleEdit = (reason: TaskCompletionReason) => {
    setEditing(reason);
    setFormName(reason.name);
    setFormDescription(reason.description || '');
    setFormColor(reason.color || COLOR_PRESETS[0]);
    setFormSortOrder(reason.sort_order ?? 0);
    setFormIsActive(reason.is_active);
    setFormOutcome(isRetention ? reason.outcome || 'cancelled' : 'neutral');
    setDialogOpen(true);
  };

  /** Persiste criação/edição (validação básica no cliente; RPC valida de novo) */
  const handleSubmit = async () => {
    const name = formName.trim();
    if (!name) return;

    await upsertReason({
      id: editing?.id,
      name: name.slice(0, 120),
      description: formDescription.trim().slice(0, 500) || null,
      color: formColor,
      sort_order: formSortOrder,
      is_active: formIsActive,
      // v1.3.0 — fora de Retenção o motivo não participa da taxa de retenção
      outcome: isRetention ? formOutcome : 'neutral',
    });
    setDialogOpen(false);
  };

  /** Exclusão com confirmação */
  const handleDelete = async (reason: TaskCompletionReason) => {
    const confirmed = await confirmDialog({
      title: 'Excluir motivo',
      description: `Deseja realmente excluir o motivo "${reason.name}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (confirmed) await deleteReason(reason.id);
  };

  // --------------------------------------------------------------- loading
  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between gap-3 p-4 border-b">
          <Skeleton className="h-8 w-56" />
          <Skeleton className="h-8 w-36" />
        </div>
        <div className="grid grid-cols-3 gap-3 px-4 py-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}
        </div>
        <Skeleton className="mx-4 h-64" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header compacto (padrão do módulo) */}
      <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold">Motivos de Conclusão — {taskModule.label}</h1>
          <p className="text-xs text-muted-foreground">
            Motivos exigidos ao concluir {taskModule.singular.toLowerCase()} deste módulo
          </p>
        </div>
        <Button size="xs" onClick={handleCreate}>
          <Plus className="mr-1.5" />
          Novo Motivo
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3 px-4 py-3">
        <MetricCard title="Total" value={kpis.total} icon={ListChecks} variant="primary" />
        <MetricCard title="Ativos" value={kpis.active} icon={CheckCircle} variant="success" />
        <MetricCard title="Usos registrados" value={kpis.used} icon={CheckCircle} variant="info" />
      </div>

      {/* Listagem */}
      <div className="px-4 pb-4">
        {reasons.length === 0 ? (
          <div className="rounded-lg border border-dashed p-10 text-center">
            <ListChecks className="mx-auto mb-3 h-8 w-8 text-muted-foreground/60" strokeWidth={1.5} />
            <p className="text-sm font-medium">Nenhum motivo cadastrado</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Cadastre os motivos que sua equipe poderá selecionar ao concluir.
            </p>
          </div>
        ) : (
          <div className="divide-y rounded-lg border">
            {reasons.map((reason) => (
              <div key={reason.id} className="flex items-center gap-3 px-4 py-3">
                <span
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: reason.color || '#64748b' }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{reason.name}</span>
                    {taskModule.type === 'retention' && (
                      <Badge
                        variant="outline"
                        className={cn('text-[10px]', OUTCOME_BADGE[reason.outcome || 'cancelled'].className)}
                      >
                        {OUTCOME_BADGE[reason.outcome || 'cancelled'].label}
                      </Badge>
                    )}
                    {!reason.is_active && (
                      <Badge variant="outline" className="text-[10px]">Inativo</Badge>
                    )}
                  </div>
                  {reason.description && (
                    <p className="truncate text-xs text-muted-foreground">{reason.description}</p>
                  )}
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {Number(reason.usage_count || 0)} uso(s)
                </span>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(reason)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive"
                  onClick={() => handleDelete(reason)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de criação/edição — padrão premium */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
          <PremiumDialogHeader
            icon={ListChecks}
            title={editing ? 'Editar Motivo' : 'Novo Motivo'}
            description={`Motivo de conclusão do módulo ${taskModule.label}`}
          />

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <div className="space-y-1.5">
              <Label htmlFor="reason-name">Nome *</Label>
              <Input
                id="reason-name"
                value={formName}
                maxLength={120}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Ex.: Cliente retido com desconto"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="reason-description">Descrição</Label>
              <Textarea
                id="reason-description"
                value={formDescription}
                maxLength={500}
                rows={3}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Detalhe quando este motivo deve ser usado"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Cor</Label>
              <div className="flex items-center gap-2">
                {COLOR_PRESETS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormColor(color)}
                    className={`h-6 w-6 rounded-full border-2 transition ${
                      formColor === color ? 'border-foreground' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    aria-label={`Selecionar cor ${color}`}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="reason-order">Ordem</Label>
                <Input
                  id="reason-order"
                  type="number"
                  min={0}
                  value={formSortOrder}
                  onChange={(e) => setFormSortOrder(Number(e.target.value) || 0)}
                />
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch checked={formIsActive} onCheckedChange={setFormIsActive} />
                <Label className="text-sm">Ativo</Label>
              </div>
            </div>

            {/* v1.3.0 — resultado do motivo (KPIs de Retenção): oculto no Jurídico */}
            {isRetention && (
              <div className="space-y-1.5 rounded-md border p-3">
                <Label className="text-sm">Resultado</Label>
                <Select value={formOutcome} onValueChange={(v) => setFormOutcome(v as TaskCompletionOutcome)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OUTCOME_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {OUTCOME_OPTIONS.find((o) => o.value === formOutcome)?.hint}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="border-t bg-muted/30 px-6 py-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving || !formName.trim()}>
              {isSaving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              {editing ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {ConfirmDialogComponent}
    </div>
  );
}
